import React, { useState, useRef, useEffect } from "react";
import { Typography, IconButton } from "@mui/material";
import {
  Chat as ChatIcon,
  AspectRatio as ResizeIcon,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from "@mui/icons-material";

const ChatLayout = ({
  children,
  title = "Centro de Mensajería",
  onClose,
  isSidebarOpen = true,
  onToggleSidebar,
  isDarkMode = false,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [size, setSize] = useState(() => {
    try {
      const saved = localStorage.getItem("chat-layout-size");
      return saved ? JSON.parse(saved) : { width: 700, height: 480 };
    } catch (e) {
      return { width: 700, height: 480 };
    }
  });
  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem("chat-layout-pos");
      return saved ? JSON.parse(saved) : { x: 300, y: 150 };
    } catch (e) {
      return { x: 300, y: 150 };
    }
  });
  const dragData = useRef(null);
  const resizeData = useRef(null);

  // Lógica de arrastre
  const startDrag = (e) => {
    if (
      e.target.closest("button") ||
      e.target.closest("input") ||
      e.target.closest(".MuiIconButton-root")
    ) {
      return;
    }
    dragData.current = {
      startX: e.clientX,
      startY: e.clientY,
      x: pos.x,
      y: pos.y,
    };
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  const onDrag = (e) => {
    if (!dragData.current) return;
    const { startX, startY, x, y } = dragData.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const nextPos = { x: x + dx, y: y + dy };
    setPos(nextPos);
    try {
      localStorage.setItem("chat-layout-pos", JSON.stringify(nextPos));
    } catch (e) {}
  };

  const stopDrag = () => {
    dragData.current = null;
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", stopDrag);
  };

  const startResize = (e) => {
    if (isMinimized) return;

    resizeData.current = {
      startX: e.clientX,
      startY: e.clientY,
      width: size.width,
      height: size.height,
    };

    document.addEventListener("mousemove", onResize);
    document.addEventListener("mouseup", stopResize);
  };

  const onResize = (e) => {
    if (!resizeData.current) return;

    const { startX, startY, width, height } = resizeData.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    const nextSize = {
      width: Math.max(500, width + dx),
      height: Math.max(350, height + dy),
    };
    setSize(nextSize);
    try {
      localStorage.setItem("chat-layout-size", JSON.stringify(nextSize));
    } catch (e) {}
  };

  const stopResize = () => {
    resizeData.current = null;
    document.removeEventListener("mousemove", onResize);
    document.removeEventListener("mouseup", stopResize);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", stopDrag);
      document.removeEventListener("mousemove", onResize);
      document.removeEventListener("mouseup", stopResize);
    };
  }, []);

  const themeVariables = isDarkMode
    ? {
        "--chat-bg-window": "#0b141a",
        "--chat-bg-sidebar": "#111b21",
        "--chat-bg-header": "#202c33",
        "--chat-bg-search-input": "#202c33",
        "--chat-bg-chat-input": "#2a3942",
        "--chat-bg-row-active": "#2a3942",
        "--chat-bg-row-hover": "#202c33",
        "--chat-border": "#222c32",
        "--chat-text-primary": "#e9edef",
        "--chat-text-secondary": "#8696a0",
        "--chat-icon": "#8696a0",
        "--chat-bg-bubble-own": "#005c4b",
        "--chat-bg-bubble-reply": "#202c33",
        "--chat-text-bubble-own": "#e9edef",
        "--chat-text-bubble-reply": "#e9edef",
        "--chat-bg-welcome": "#222c32",
        "--chat-text-welcome-title": "#e9edef",
        "--chat-text-welcome-sub": "#8696a0",
        "--chat-window-border": "#222c32",
        "--chat-window-bg": "#111b21",
        "--chat-bg-dragbar": "#202c33",
      }
    : {
        "--chat-bg-window": "#e6efe2",
        "--chat-bg-sidebar": "#ffffff",
        "--chat-bg-header": "#f0f2f5",
        "--chat-bg-search-input": "#f0f2f5",
        "--chat-bg-chat-input": "#ffffff",
        "--chat-bg-row-active": "#eae6df",
        "--chat-bg-row-hover": "#f5f6f6",
        "--chat-border": "#e9edef",
        "--chat-text-primary": "#111b21",
        "--chat-text-secondary": "#667781",
        "--chat-icon": "#54656f",
        "--chat-bg-bubble-own": "#d9fdd3",
        "--chat-bg-bubble-reply": "#ffffff",
        "--chat-text-bubble-own": "#111b21",
        "--chat-text-bubble-reply": "#111b21",
        "--chat-bg-welcome": "#f8f9fa",
        "--chat-text-welcome-title": "#41525d",
        "--chat-text-welcome-sub": "#8696a0",
        "--chat-window-border": "#e9edef",
        "--chat-window-bg": "#ffffff",
        "--chat-bg-dragbar": "#00a884",
      };

  return (
    <div
      style={{
        ...styles.window,
        ...themeVariables,
        left: pos.x,
        top: pos.y,
        height: isMinimized ? "480px" : `${size.height}px`,
        width: isMinimized ? "260px" : `${size.width}px`,
        resize: isMinimized ? "none" : "none",
        minWidth: isMinimized ? "260px" : "500px",
        minHeight: isMinimized ? "480px" : "350px",
      }}
    >
      {/* Barra de arrastre superior */}
      <div style={styles.dragBar} onMouseDown={startDrag}>
        <div style={styles.dragTitleContainer}>
          {onToggleSidebar && !isMinimized && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onToggleSidebar();
              }}
              size="small"
              style={styles.toggleSidebarBtn}
              title={isSidebarOpen ? "Ocultar menú lateral" : "Mostrar menú lateral"}
            >
              {isSidebarOpen ? (
                <MenuOpenIcon style={styles.toggleIcon} />
              ) : (
                <MenuIcon style={styles.toggleIcon} />
              )}
            </IconButton>
          )}
          <ChatIcon style={styles.dragIcon} />
          <Typography style={styles.dragTitle}>
            {isMinimized ? "Chat (Minimizado)" : title}
          </Typography>
        </div>
        <div style={styles.dragActions} onMouseDown={(e) => e.stopPropagation()}>
          <button
            style={styles.minimizeBtn}
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? "Restaurar" : "Minimizar"}
          >
            {isMinimized ? "🗖" : "🗕"}
          </button>
          {onClose && (
            <button style={styles.closeBtn} onClick={onClose} title="Cerrar">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Renderiza el contenido interno */}
      <div style={styles.body}>{children}</div>

      {/* Tirador visual de Redimensionamiento (solo si no está minimizado) */}
      {!isMinimized && <ResizeIcon style={styles.resizeHandle} onMouseDown={startResize} />}
    </div>
  );
};

const styles = {
  window: {
    position: "fixed",
    backgroundColor: "var(--chat-window-bg, #ffffff)",
    borderRadius: "12px",
    boxShadow: "0 12px 36px rgba(11,20,26,.18)",
    display: "flex",
    flexDirection: "column",
    zIndex: 9999,
    overflow: "hidden",
    border: "1px solid var(--chat-window-border, #e9edef)",
    resize: "both",
    minWidth: "500px",
    minHeight: "350px",
  },
  dragBar: {
    height: "50px",
    backgroundColor: "var(--chat-bg-dragbar, #00a884)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    cursor: "grab",
    userSelect: "none",
    color: "#ffffff",
    borderBottom: "1px solid var(--chat-border, rgba(0,0,0,0.05))",
  },
  dragTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  dragIcon: {
    fontSize: "20px",
  },
  dragTitle: {
    fontSize: "14px",
    fontWeight: "600",
    fontFamily: "Raleway, sans-serif",
  },
  dragActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  minimizeBtn: {
    border: "none",
    background: "transparent",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "14px",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.8,
    ":hover": {
      opacity: 1,
    },
  },
  closeBtn: {
    border: "none",
    background: "transparent",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "14px",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.8,
    ":hover": {
      opacity: 1,
    },
  },
  body: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
    height: "calc(100% - 50px)",
    width: "100%",
  },
  resizeHandle: {
    position: "absolute",
    bottom: "2px",
    right: "2px",
    cursor: "nwse-resize",
    pointerEvents: "auto",
    color: "#8696a0",
    fontSize: "16px",
    transform: "rotate(90deg)",
    zIndex: 10000,
  },
  toggleSidebarBtn: {
    color: "#ffffff",
    padding: "4px",
    marginRight: "2px",
  },
  toggleIcon: {
    fontSize: "20px",
  },
};

export default ChatLayout;
